import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShoppingCart,
  faSignOutAlt,
  faBook,
  faUsers,
  faFileInvoice,
  faFileAlt,
  faCalculator,
  faTruckMoving,
  faWarehouse,
  faUserCog,
  faClipboardCheck,
  faBars,
  faTimes,
  faChevronRight,
  faChevronLeft,
  faGaugeHigh,
  faCircleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Outlet, useNavigate, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { getPendingApprovalsCount } from '../services/api';
import './Layout.css';

function Layout() {
  const { user, logout, hasPermission, permissions, loading } = useAuth();
  const { tenantConfig } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    try { return localStorage.getItem('sidebarPinned') === 'true'; } catch { return false; }
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const hoverTimeoutRef = useRef(null);
  const userMenuRef = useRef(null);

  const isExpanded = sidebarPinned || sidebarOpen;

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) { setSidebarOpen(false); setSidebarPinned(false); }
    };
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar on mobile navigation
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  // Track last route in session
  useEffect(() => {
    const validRoutes = ['/bols', '/rate-calculator', '/inventory', '/orders', '/ext-orders',
      '/warehouse-receipts', '/invoices', '/profile', '/catalog', '/accounts', '/users', '/pending-approvals', '/dashboard'];
    if (validRoutes.some(r => location.pathname.includes(r)) && user) {
      sessionStorage.setItem('lastNavigationRoute', location.pathname);
    }
  }, [location.pathname, user]);

  // Pending approvals polling
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (hasPermission('inventory', 'approve')) {
        const count = await getPendingApprovalsCount();
        setPendingApprovalsCount(count);
      }
    };
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 30000);
    const handleRefresh = () => fetchPendingCount();
    window.addEventListener('refreshPendingCount', handleRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshPendingCount', handleRefresh);
    };
  }, [hasPermission, permissions]);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cleanup hover timeout
  useEffect(() => {
    return () => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); };
  }, []);

  if (!user) {
    if (loading) return (
      <div className="layout-loading">
        <div className="layout-loading-spinner" />
      </div>
    );
    return <Navigate to="/login" />;
  }

  const getTenantLink = (path) => {
    const tenantPrefix = params.tenantPrefix;
    return tenantPrefix ? `/${tenantPrefix}${path}` : path;
  };

  const handleNavigation = (path) => {
    navigate(getTenantLink(path));
    if (isMobile) setSidebarOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate(params.tenantPrefix ? `/${params.tenantPrefix}/login` : '/login');
  };

  const togglePin = () => {
    const newPinned = !sidebarPinned;
    setSidebarPinned(newPinned);
    try { localStorage.setItem('sidebarPinned', String(newPinned)); } catch { /* */ }
  };

  const handleMouseEnter = () => {
    if (!isMobile && !sidebarPinned) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => setSidebarOpen(true), 100);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && !sidebarPinned) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => setSidebarOpen(false), 800);
    }
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/dashboard')) return 'Dashboard';
    if (path.includes('/bols')) return 'Bills of Lading';
    if (path.includes('/rate-calculator')) return 'Rate Calculator';
    if (path.includes('/inventory')) return 'Inventory';
    if (path.includes('/orders/')) return 'Order Detail';
    if (path.includes('/orders')) return 'Transactions';
    if (path.includes('/ext-orders')) return 'External Orders';
    if (path.includes('/warehouse-receipts')) return 'Warehouse Receipts';
    if (path.includes('/invoices')) return 'Invoices';
    if (path.includes('/catalog')) return 'Catalog';
    if (path.includes('/accounts')) return 'Accounts';
    if (path.includes('/users')) return 'User Management';
    if (path.includes('/pending-approvals')) return 'Pending Approvals';
    if (path.includes('/profile')) return 'Profile';
    return 'Portal';
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.name) return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    if (user.username) return user.username.slice(0, 2).toUpperCase();
    return 'U';
  };

  const isPathActive = (path) => location.pathname.includes(path);

  // Navigation groups
  const navGroups = [
    {
      id: 'transportation',
      module: 'transportation',
      icon: faTruckMoving,
      label: 'Transportation',
      items: [
        { icon: faTruckMoving, label: 'Bills of Lading', path: '/bols' },
        { icon: faCalculator, label: 'Rate Calculator', path: '/rate-calculator' },
      ],
    },
    {
      id: 'inventory',
      module: 'inventory',
      icon: faWarehouse,
      label: 'Inventory',
      items: [
        { icon: faWarehouse, label: 'Inventory', path: '/inventory' },
        { icon: faShoppingCart, label: 'Transactions', path: '/orders' },
        { icon: faBook, label: 'Catalog', path: '/catalog' },
        ...(hasPermission('inventory', 'approve')
          ? [{ icon: faClipboardCheck, label: 'Pending Approvals', path: '/pending-approvals', badge: pendingApprovalsCount }]
          : []),
      ],
    },
    {
      id: 'ext_orders',
      module: 'ext_orders',
      icon: faFileAlt,
      label: 'External Orders',
      items: [{ icon: faFileAlt, label: 'External Orders', path: '/ext-orders' }],
    },
    {
      id: 'warehouse_receipts',
      module: 'warehouse_receipts',
      icon: faFileInvoice,
      label: 'Warehouse Receipts',
      items: [{ icon: faFileInvoice, label: 'Warehouse Receipts', path: '/warehouse-receipts' }],
    },
    // invoices nav temporarily removed

    {
      id: 'accounts',
      module: 'accounts',
      icon: faUsers,
      label: 'Accounts',
      items: [{ icon: faUsers, label: 'Accounts', path: '/accounts' }],
    },
  ];

  return (
    <div className="layout-root">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── SIDEBAR ── */}
      <aside
        className={`sidebar ${isExpanded ? 'sidebar--expanded' : 'sidebar--collapsed'}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Logo */}
        <div
          className="sidebar-logo"
          onClick={() => isMobile && setSidebarOpen(!sidebarOpen)}
          title={isMobile ? 'Toggle navigation' : undefined}
        >
          <img
            src={isExpanded
              ? (tenantConfig.loginLogo || '/bannernew.png')
              : (tenantConfig.navLogo || '/globe2.png')}
            alt="Logo"
            className="sidebar-logo-img"
            onError={(e) => { e.target.src = isExpanded ? '/bannernew.png' : '/globe2.png'; }}
          />
        </div>

        {/* Nav */}
        <nav className="sidebar-nav" aria-label="Main navigation">
          {/* Dashboard link (always visible) */}
          <button
            className={`nav-item ${isPathActive('/dashboard') ? 'nav-item--active' : ''}`}
            onClick={() => handleNavigation('/dashboard')}
            title={!isExpanded ? 'Dashboard' : undefined}
          >
            <FontAwesomeIcon icon={faGaugeHigh} className="nav-item-icon" />
            {isExpanded && <span className="nav-item-label">Dashboard</span>}
          </button>

          {navGroups.map((group) => {
            if (!hasPermission(group.module)) return null;
            const isGroupActive = group.items.some(i => isPathActive(i.path));
            const singleItem = group.items.length === 1;

            if (singleItem) {
              const item = group.items[0];
              return (
                <button
                  key={group.id}
                  className={`nav-item ${isPathActive(item.path) ? 'nav-item--active' : ''}`}
                  onClick={() => handleNavigation(item.path)}
                  title={!isExpanded ? group.label : undefined}
                >
                  <FontAwesomeIcon icon={group.icon} className="nav-item-icon" />
                  {isExpanded && <span className="nav-item-label">{group.label}</span>}
                </button>
              );
            }

            return (
              <div key={group.id} className={`nav-group ${isGroupActive ? 'nav-group--active' : ''}`}>
                {isExpanded && (
                  <div className="nav-group-label">
                    <FontAwesomeIcon icon={group.icon} className="nav-item-icon" />
                    <span>{group.label}</span>
                  </div>
                )}
                {group.items.map((item) => (
                  <button
                    key={item.path}
                    className={`nav-item ${singleItem ? '' : 'nav-item--sub'} ${isPathActive(item.path) ? 'nav-item--active' : ''}`}
                    onClick={() => handleNavigation(item.path)}
                    title={!isExpanded ? item.label : undefined}
                  >
                    <FontAwesomeIcon icon={item.icon} className="nav-item-icon" />
                    {isExpanded && (
                      <span className="nav-item-label">
                        {item.label}
                        {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
                      </span>
                    )}
                    {!isExpanded && item.badge > 0 && <span className="nav-badge nav-badge--dot" />}
                  </button>
                ))}
              </div>
            );
          })}

          {/* Admin: User Management */}
          {permissions?.isAdmin && (
            <button
              className={`nav-item ${isPathActive('/users') ? 'nav-item--active' : ''}`}
              onClick={() => handleNavigation('/users')}
              title={!isExpanded ? 'User Management' : undefined}
            >
              <FontAwesomeIcon icon={faUserCog} className="nav-item-icon" />
              {isExpanded && <span className="nav-item-label">User Management</span>}
            </button>
          )}
        </nav>

        {/* Sidebar footer */}
        <div className="sidebar-footer">
          {!isMobile && (
            <button
              className="sidebar-pin-btn"
              onClick={togglePin}
              title={sidebarPinned ? 'Unpin sidebar' : 'Keep sidebar open'}
            >
              <FontAwesomeIcon icon={sidebarPinned ? faChevronLeft : faChevronRight} className="nav-item-icon" />
              {isExpanded && <span className="nav-item-label">{sidebarPinned ? 'Unpin' : 'Keep Open'}</span>}
            </button>
          )}

          <button
            className={`nav-item ${isPathActive('/profile') ? 'nav-item--active' : ''}`}
            onClick={() => handleNavigation('/profile')}
            title={!isExpanded ? 'Profile' : undefined}
          >
            <FontAwesomeIcon icon={faUserCog} className="nav-item-icon" />
            {isExpanded && <span className="nav-item-label">Profile</span>}
          </button>

          <button
            className="nav-item nav-item--logout"
            onClick={handleLogout}
            title={!isExpanded ? 'Sign Out' : undefined}
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="nav-item-icon" />
            {isExpanded && <span className="nav-item-label">Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* ── MAIN AREA ── */}
      <div className={`main-wrapper ${isExpanded && !isMobile ? 'main-wrapper--expanded' : 'main-wrapper--collapsed'}`}>

        {/* Top header */}
        <header className="top-header">
          <div className="top-header-left">
            {isMobile && (
              <button
                className="mobile-menu-btn"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Toggle navigation"
              >
                <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} />
              </button>
            )}
            <div className="page-title">
              <h1>{getPageTitle()}</h1>
              {tenantConfig.tenantName && !tenantConfig.isDefault && (
                <span className="page-tenant-badge">{tenantConfig.tenantName}</span>
              )}
            </div>
          </div>

          <div className="top-header-right">
            {/* Pending approvals alert */}
            {pendingApprovalsCount > 0 && hasPermission('inventory', 'approve') && (
              <button
                className="header-alert-btn"
                onClick={() => navigate(getTenantLink('/pending-approvals'))}
                title={`${pendingApprovalsCount} orders pending approval`}
              >
                <FontAwesomeIcon icon={faCircleExclamation} />
                <span className="header-alert-count">{pendingApprovalsCount}</span>
                <span className="header-alert-label">Approvals</span>
              </button>
            )}

            {/* User menu */}
            <div className="user-menu-wrapper" ref={userMenuRef}>
              <button
                className="user-avatar-btn"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-label="User menu"
                aria-expanded={userMenuOpen}
              >
                <div className="user-avatar">{getUserInitials()}</div>
                <span className="user-name-display">{user.name || user.username}</span>
                <FontAwesomeIcon icon={faChevronRight} className={`user-chevron ${userMenuOpen ? 'user-chevron--open' : ''}`} />
              </button>

              {userMenuOpen && (
                <div className="user-dropdown" role="menu">
                  <div className="user-dropdown-header">
                    <div className="user-avatar user-avatar--lg">{getUserInitials()}</div>
                    <div className="user-dropdown-info">
                      <div className="user-dropdown-name">{user.name || user.username}</div>
                      <div className="user-dropdown-role">
                        {permissions?.isAdmin ? 'Administrator' : 'Portal User'}
                      </div>
                      {user.customerName && (
                        <div className="user-dropdown-company">{user.customerName}</div>
                      )}
                    </div>
                  </div>
                  <div className="user-dropdown-divider" />
                  <button
                    className="user-dropdown-item"
                    onClick={() => { handleNavigation('/profile'); setUserMenuOpen(false); }}
                    role="menuitem"
                  >
                    <FontAwesomeIcon icon={faUserCog} />
                    <span>Profile Settings</span>
                  </button>
                  <div className="user-dropdown-divider" />
                  <button
                    className="user-dropdown-item user-dropdown-item--danger"
                    onClick={handleLogout}
                    role="menuitem"
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Pending approvals banner */}
        {pendingApprovalsCount > 0 && hasPermission('inventory', 'approve') && (
          <div
            className="approval-banner"
            onClick={() => navigate(getTenantLink('/pending-approvals'))}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate(getTenantLink('/pending-approvals'))}
          >
            <FontAwesomeIcon icon={faClipboardCheck} className="approval-banner-icon" />
            <span>
              <strong>{pendingApprovalsCount} order{pendingApprovalsCount !== 1 ? 's' : ''}</strong> awaiting approval
            </span>
            <span className="approval-banner-cta">Review Now →</span>
          </div>
        )}

        {/* Page content */}
        <main className="main-content" id="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
